import { getZonedDate } from '@eika-infrastruktur/date';
import {
    useQuery,
    useMutation,
    useQueryClient,
    MutationOptions,
    QueryOptions,
    QueryResult,
    UseMutationResult,
    useSuspenseQuery,
    UseSuspenseQueryOptions,
} from '@eika-infrastruktur/react-query';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import type {
    BatchEfakturaItemSigningResult,
    CreateEfakturaAgreementRequest,
    DynamicLinkId,
    EFaktura,
    EFakturaAgreementResponse,
    EFakturaCreditor,
    EFakturaCreditorId,
    EFakturaId,
    Epost,
} from '../generated-types';
import { getBatchedEfakturaSigningResult } from '../services/batchedEfakturaService';
import { getEFakturas, getInvoice } from '../services/eFakturaService';
import {
    blockEFakturaCreditor,
    createEfakturaAgreement,
    disableEFakturaAgreement,
    disableEmailNotifications,
    enableEFakturaAgreement,
    enableEmailNotifications,
    getEFakturaAgreement,
    getEFakturaCreditors,
    unblockEFakturaCreditor,
} from '../services/efakturaAgreementService';

const EFAKTURA_AGREEMENT_QUERY_KEY = ['efaktura-agreement'];
const EFAKTURA_CREDITOR_QUERY_KEY = ['efaktura-creditors'];

export const EFAKTURA_QUERY_KEY = ['efakturas'];

const compareDate = (a: string, b: string) => getZonedDate(a).getTime() - getZonedDate(b).getTime();
const sortByDate = (a: EFaktura, b: EFaktura): number => {
    const sort = compareDate(a.paymentDate, b.paymentDate);
    if (sort === 0 && a.notificationDate && b.notificationDate) {
        return compareDate(a.notificationDate, b.notificationDate);
    }
    return sort;
};

export const notAvtalegiroEfakturas = (eFaktruas: EFaktura[]) =>
    eFaktruas.filter((x) => x.efakturaType !== 'EFAKTURA_WITH_AVTALEGIRO');

export const useSuspendedEFakturas = (
    options: Omit<UseSuspenseQueryOptions<EFaktura[], RequestExceptionWithResponse>, 'queryKey'> = {},
) =>
    useSuspenseQuery({
        ...options,
        queryKey: EFAKTURA_QUERY_KEY,
        queryFn: getEFakturas,
        select: (eFakturas) => {
            const filtered = options.select ? options.select(eFakturas) : eFakturas;
            return filtered.sort(sortByDate);
        },
    });

const useEfaktura = (options: Omit<QueryOptions<EFaktura[]>, 'queryKey'> = {}): QueryResult<EFaktura[]> =>
    useQuery<EFaktura[]>({
        ...options,
        queryKey: EFAKTURA_QUERY_KEY,
        queryFn: getEFakturas,
        select: (eFakturas) => {
            const filtered = options.select ? options.select(eFakturas) : eFakturas;
            return filtered.sort(sortByDate);
        },
    });

export const useRefreshEfaktura = () => {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: EFAKTURA_QUERY_KEY });
};

export const useEfakturaLink = (efakturaId?: EFakturaId): QueryResult<string> =>
    useQuery({
        queryKey: ['efaktura-link', efakturaId?.value],
        queryFn: () => getInvoice(efakturaId!),
        enabled: !!efakturaId,
        staleTime: Infinity,
    });

type EfakturaAgreementQueryResponse = {
    data: EFakturaAgreementResponse | undefined;
    isLoading: boolean;
    isChangingAgreement: boolean;
    isSuccess: boolean;
    error: RequestExceptionWithResponse | null;
    createAgreement: (createAgreement?: CreateEfakturaAgreementRequest) => void;
    enableAgreement: () => void;
    disableAgreement: () => void;
};

export const useEfakturaAgreement = (onSuccess?: (value: boolean) => void): EfakturaAgreementQueryResponse => {
    const queryClient = useQueryClient();
    const createAgreement = useMutation<
        EFakturaAgreementResponse,
        RequestExceptionWithResponse,
        CreateEfakturaAgreementRequest | undefined
    >({
        mutationKey: ['efaktura-agreement-create'],
        mutationFn: createEfakturaAgreement,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: EFAKTURA_AGREEMENT_QUERY_KEY }),
    });

    const currentAgreement = useQuery<EFakturaAgreementResponse>({
        queryKey: EFAKTURA_AGREEMENT_QUERY_KEY,
        queryFn: getEFakturaAgreement,
        onSuccess: (data) => onSuccess?.(data.enabled),
    });

    const disableAgreement = useMutation<unknown, RequestExceptionWithResponse>({
        mutationFn: disableEFakturaAgreement,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: EFAKTURA_AGREEMENT_QUERY_KEY }),
    });
    const enableAgreement = useMutation<unknown, RequestExceptionWithResponse>({
        mutationFn: enableEFakturaAgreement,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: EFAKTURA_AGREEMENT_QUERY_KEY }),
    });
    return {
        data: currentAgreement.data,
        isLoading: currentAgreement.isLoading || createAgreement.isPending,
        isChangingAgreement: disableAgreement.isPending || enableAgreement.isPending,
        isSuccess: disableAgreement.isSuccess || enableAgreement.isSuccess,
        error: currentAgreement.error || disableAgreement.error || enableAgreement.error || createAgreement.error,
        createAgreement: createAgreement.mutate,
        enableAgreement: enableAgreement.mutate,
        disableAgreement: disableAgreement.mutate,
    };
};

type EfakturaNotificationResult = {
    isSaving: boolean;
    error: RequestExceptionWithResponse | null;
    isSuccess: boolean;
    enableNotification: UseMutationResult<unknown, RequestExceptionWithResponse, Epost>;
    disableNotification: UseMutationResult<unknown, RequestExceptionWithResponse, void>;
};

export const useEfakturaNotification = (): EfakturaNotificationResult => {
    const queryClient = useQueryClient();
    const enableNotification = useMutation<unknown, RequestExceptionWithResponse, Epost>({
        mutationFn: enableEmailNotifications,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: EFAKTURA_AGREEMENT_QUERY_KEY }),
    });
    const disableNotification = useMutation<unknown, RequestExceptionWithResponse>({
        mutationFn: disableEmailNotifications,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: EFAKTURA_AGREEMENT_QUERY_KEY }),
    });

    return {
        isSaving: enableNotification.isPending || disableNotification.isPending,
        error: enableNotification.error || disableNotification.error,
        isSuccess: enableNotification.isSuccess,
        enableNotification,
        disableNotification,
    };
};

export const nonBlockedCreditors = (creditors: EFakturaCreditor[]) =>
    creditors.filter((creditor) => !creditor.isBlocked);

export const useBlockEfakturaCreditor = (
    options?: Partial<MutationOptions<unknown, RequestExceptionWithResponse, EFakturaCreditorId>>,
) => {
    const queryClient = useQueryClient();
    return useMutation<unknown, RequestExceptionWithResponse, EFakturaCreditorId>({
        ...options,
        mutationKey: ['block-efaktura-creditor'],
        mutationFn: blockEFakturaCreditor,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: EFAKTURA_CREDITOR_QUERY_KEY });
            options?.onSuccess?.(data, variables, context);
        },
    });
};

export const useUnblockEfakturaCreditor = (
    options?: Partial<MutationOptions<unknown, RequestExceptionWithResponse, EFakturaCreditorId>>,
) => {
    const queryClient = useQueryClient();
    return useMutation<unknown, RequestExceptionWithResponse, EFakturaCreditorId>({
        ...options,
        mutationKey: ['unblock-efaktura-creditor'],
        mutationFn: unblockEFakturaCreditor,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: EFAKTURA_CREDITOR_QUERY_KEY });
            options?.onSuccess?.(data, variables, context);
        },
    });
};

type EfakturaCreditorResult = {
    data: EFakturaCreditor[];
} & Omit<QueryResult<EFakturaCreditor[]>, 'data'>;

export const useEfakturaCreditors = (options?: Partial<QueryOptions<EFakturaCreditor[]>>): EfakturaCreditorResult => {
    const { data: creditors = [], ...query } = useQuery({
        ...options,
        queryKey: EFAKTURA_CREDITOR_QUERY_KEY,
        queryFn: getEFakturaCreditors,
    });
    return { data: creditors, ...query };
};

export const useBatchedEfakturaSigningResult = (dynamicLinkId: DynamicLinkId | undefined) =>
    useQuery<BatchEfakturaItemSigningResult[]>({
        queryKey: ['batched-efaktura-signing-result', dynamicLinkId?.value],
        queryFn: () => getBatchedEfakturaSigningResult(dynamicLinkId!),
        enabled: !!dynamicLinkId,
    });

export default useEfaktura;
